import React from "react";
import { useState } from "react";
import { FiMail, FiAlertCircle, FiCheckCircle, FiInfo, FiTrash2, FiUser, FiPackage } from "react-icons/fi";
import { Link } from "react-router-dom";

const GAMES = require("../data/games.json");

function Unsubscribe() {
    const [email, setEmail] = useState("");
    const [userId, setUserId] = useState("");
    const [gameId, setGameId] = useState("");
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Convert games object to array for dropdown
    const gamesList = Object.values(GAMES);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email) {
            setError("Please enter your email address.");
            return;
        }

        if (!userId) {
            setError("Please enter your user ID.");
            return;
        }

        if (!gameId) {
            setError("Please select a game.");
            return;
        }

        if (!consent) {
            setError("Please confirm your consent to unsubscribe.");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('/api/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, userId, gameId }),
            });

            if (response.ok) {
                setDone(true);
            } else {
                setError("Failed to process unsubscribe request. Please try again or contact us directly.");
            }
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setError("Failed to process unsubscribe request. Please try again or contact us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="pt-20 pb-16 min-h-screen bg-gradient-to-b from-[#021428] to-[#011226]">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
                        <FiTrash2 className="text-3xl text-red-400" />
                    </div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                        Account Deletion Request
                    </h1>
                    <p className="text-blue-200 text-base sm:text-lg">
                        Data Consent & Unsubscribe
                    </p>
                </div>

                {done ? (
                    /* Success State */
                    <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/30 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <FiCheckCircle className="text-4xl text-green-400" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-xl sm:text-2xl font-bold mb-3 text-green-300">
                                    Request Successfully Submitted
                                </h2>
                                <p className="text-green-100 leading-relaxed mb-4">
                                    Your request for complete account deletion has been successfully queued.
                                </p>
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <FiInfo className="text-xl text-blue-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <h3 className="font-semibold text-blue-300 mb-2">Important: 10-Day Retention Period</h3>
                                            <ul className="text-blue-100 space-y-1 text-sm sm:text-base">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-blue-400 mt-1">•</span>
                                                    <span>A 10-day data retention period is provided.</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-blue-400 mt-1">•</span>
                                                    <span>During this period, you may cancel your request to prevent deletion.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <Link 
                                    to="/" 
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
                                >
                                    Return to Home
                                </Link>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Form State */
                    <div className="bg-gradient-to-br from-[#011426] to-[#001424] rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg">
                        <div className="mb-6">
                            <p className="text-blue-100 leading-relaxed mb-6">
                                By submitting this form, your request for complete account deletion will be successfully queued.
                            </p>

                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <FiAlertCircle className="text-xl text-yellow-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold text-yellow-300 mb-2">Important: 10-Day Retention Period</h3>
                                        <ul className="text-yellow-100 space-y-1 text-sm sm:text-base">
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-400 mt-1">•</span>
                                                <span>A 10-day data retention period is provided.</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-400 mt-1">•</span>
                                                <span>During this period, you may cancel your request in your favor to prevent deletion.</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Game Selection */}
                            <div>
                                <label htmlFor="gameId" className="block text-sm font-semibold text-blue-200 mb-2">
                                    Select Game
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FiPackage className="text-blue-400" />
                                    </div>
                                    <select
                                        id="gameId"
                                        value={gameId}
                                        onChange={(e) => setGameId(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-[#021428] border border-blue-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                                        required
                                    >
                                        <option value="" className="bg-[#021428] text-blue-400">Select a game...</option>
                                        {gamesList.map((game) => (
                                            <option key={game.id} value={game.id} className="bg-[#021428] text-white">
                                                {game.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* User ID Input */}
                            <div>
                                <label htmlFor="userId" className="block text-sm font-semibold text-blue-200 mb-2">
                                    User ID
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FiUser className="text-blue-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="userId"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-[#021428] border border-blue-500/30 rounded-lg text-white placeholder-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        placeholder="Enter your user ID"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email Input */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-blue-200 mb-2">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FiMail className="text-blue-400" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-[#021428] border border-blue-500/30 rounded-lg text-white placeholder-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        placeholder="Enter your email address"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Consent Checkbox */}
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id="consent"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-1 w-5 h-5 text-red-600 bg-[#021428] border-red-500/50 rounded focus:ring-2 focus:ring-red-500 cursor-pointer"
                                    />
                                    <label htmlFor="consent" className="flex-1 text-blue-100 leading-relaxed cursor-pointer">
                                        <span className="font-semibold text-red-300">✍️ Final Data Deletion Consent</span>
                                        <br />
                                        <span className="text-sm sm:text-base">
                                            I hereby give my full consent to the final and complete deletion of all my associated data from the Skylapse systems.
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                                    <FiAlertCircle className="text-xl text-red-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-red-200 text-sm sm:text-base">{error}</p>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-3 px-6 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin">⏳</span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 />
                                        Confirm Deletion Request
                                    </>
                                )}
                            </button>

                            {/* Back Link */}
                            <div className="text-center">
                                <Link 
                                    to="/" 
                                    className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                                >
                                    ← Back to Home
                                </Link>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Unsubscribe;